import React, { useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { LoadingSpinner, ErrorMessage, EmptyState, ChartControls } from '../ui';
import { useCustomerBalance, SectionProps } from '../../hooks/useCustomerAnalyticsSections';
import { useTheme } from '../../contexts/ThemeContext';
import { getChartJsOptions, getChartColors, getChartBackgroundColor } from '../../utils/themeHelpers';
import { formatNumber } from '../../utils/formatters';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import useChartGuide from '../../hooks/useChartGuide';
import { CHART_GUIDES } from '../../constants/chartGuides';

interface CustomerBalanceChartProps extends SectionProps {
  branchId: number;
  months?: number;
}

const CustomerBalanceChart: React.FC<CustomerBalanceChartProps> = ({
  branchId,
  months = 12,
  onError,
  showLoading = true,
  compact = false
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const { currentTheme } = useTheme();
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  const { data: balanceData, isLoading, isError, error, refetch } = useCustomerBalance(branchId, months);

  // Component metadata
  const componentId = 'customer-balance-chart';
  const componentTitle = 'رشد مشتریان';

  // Handle error callback
  React.useEffect(() => {
    if (isError && error && onError) {
      onError(error as Error);
    }
  }, [isError, error, onError]);

  if (isLoading && showLoading) {
    return (
      <div className="card-base card-padding animate-enter animate-delay-3">
        <div className="flex flex-col items-center justify-center h-[350px]">
          <LoadingSpinner
            variant="minimal"
            size="lg"
            message="در حال بارگذاری تراز…"
          />
        </div>
      </div>
    );
  }

  if (isError && error) {
    return (
      <div className="card-base card-padding">
        <ErrorMessage 
          message="خطا در دریافت رشد مشتریان"
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  if (!balanceData || balanceData.length === 0) {
    return (
      <div className="card-base card-padding">
        <EmptyState />
      </div>
    );
  }

  // Calculate cumulative balance in chronological order
  let cumulative = 0;
  const cumulativeData = balanceData.map(item => {
    return item.netChange;
    // return cumulative;
  });

  const chartData = {
    labels: balanceData.map(item => item.month),
    datasets: [
      {
        label: 'تراز تجمعی مشتریان',
        data: cumulativeData,
        borderColor: getChartColors(currentTheme)[0],
        backgroundColor: getChartColors(currentTheme)[0],
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: getChartColors(currentTheme)[0],
        tension: 0.4,
        fill: false
      }
    ]
  };

  return (
    <div ref={chartRef} className="card-base card-padding animate-enter animate-delay-3">
      <div className="mb-6">
        <ChartControls
          componentId={componentId}
          componentTitle={componentTitle}
          componentType="chart"
          dashboardSource="customerAnalytics"
          onInfo={() => openGuide(CHART_GUIDES.customerBalanceChart)}
          onFavorite={() => handleFavoriteToggle({
            componentId,
            componentTitle,
            dashboardSource: 'customerAnalytics',
            componentType: 'chart',
            description: 'تحلیل تراز تجمعی مشتریان در طول زمان',
            props: { branchId, months, compact },
            gridLayout: {
              containerClass: 'col-span-1'
            }
          })}
          isFavorited={isFavorited(componentId)}
          chartRef={chartRef}
        />
      </div>
      <h3 className="title-card">
        رشد مشتریان
      </h3>
      <div style={{ height: 350, width: '100%', padding: '20px 0' }}>
        <Line
          data={chartData}
          options={{
            ...getChartJsOptions(currentTheme),
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              ...getChartJsOptions(currentTheme).plugins,
              tooltip: {
                ...getChartJsOptions(currentTheme).plugins?.tooltip,
                callbacks: {
                  label: function(context: any) {
                    const label = context.dataset.label || '';
                    const value = formatNumber(context.parsed.y);
                    const separator = '\u00A0:\u00A0';
                    return `${label}${separator}${value}`;
                  }
                }
              }
            },
            scales:{
                                      ...getChartJsOptions(currentTheme).scales,
                                      x:{
                                        ...getChartJsOptions(currentTheme).scales.x,
                                        ticks: {
                                        ...getChartJsOptions(currentTheme).scales?.x?.ticks,
                                        autoSkip: false,
                                        maxRotation: 45,
                                        minRotation: 45,
                                      },
                                      }
                                    }
          }}
        />
      </div>
    </div>
  );
};

export default CustomerBalanceChart;